import React, { useState, useEffect } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useDateFormat } from '../contexts/DateFormatContext';

/**
 * Custom Date Input that respects user's date format preference
 * Converts between display format and ISO format for backend
 */
const DateInput = ({ 
  label, 
  value, // ISO format YYYY-MM-DD
  onChange, // Callback receives ISO format
  className = '',
  required = false,
  placeholder,
  ...props 
}) => {
  const { dateFormat, formatDate, DATE_FORMATS } = useDateFormat();
  const [displayValue, setDisplayValue] = useState('');
  
  // Get format info
  const formatInfo = DATE_FORMATS[dateFormat] || DATE_FORMATS['MM/DD/YYYY'];
  
  // Convert ISO date to display format
  useEffect(() => {
    if (value) {
      try {
        const date = new Date(value + 'T00:00:00'); // Add time to avoid timezone issues
        if (!isNaN(date.getTime())) {
          setDisplayValue(formatDate(date));
        }
      } catch (e) {
        setDisplayValue('');
      }
    } else {
      setDisplayValue('');
    }
  }, [value, formatDate]);

  // Parse display format to ISO format
  const parseDate = (displayStr) => {
    if (!displayStr || displayStr.length < 8) return '';
    
    let day, month, year;
    
    // Remove non-numeric characters
    const cleaned = displayStr.replace(/[^\d]/g, '');
    
    switch (dateFormat) {
      case 'DD/MM/YYYY':
      case 'DD.MM.YYYY':
        day = cleaned.substring(0, 2);
        month = cleaned.substring(2, 4);
        year = cleaned.substring(4, 8);
        break;
      case 'MM/DD/YYYY':
        month = cleaned.substring(0, 2);
        day = cleaned.substring(2, 4);
        year = cleaned.substring(4, 8);
        break;
      case 'YYYY-MM-DD':
        year = cleaned.substring(0, 4);
        month = cleaned.substring(4, 6);
        day = cleaned.substring(6, 8);
        break;
      default:
        month = cleaned.substring(0, 2);
        day = cleaned.substring(2, 4);
        year = cleaned.substring(4, 8);
    }
    
    // Validate
    if (year && month && day && year.length === 4) {
      const monthNum = parseInt(month);
      const dayNum = parseInt(day);
      const yearNum = parseInt(year);
      
      if (monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31 && yearNum >= 1900 && yearNum <= 2100) {
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
    
    return '';
  };

  const handleChange = (e) => {
    const input = e.target.value;
    setDisplayValue(input);
    
    // Try to parse and send ISO format
    const isoDate = parseDate(input);
    if (isoDate) {
      onChange({ target: { value: isoDate } });
    } else if (input === '') {
      onChange({ target: { value: '' } });
    }
  };

  const handleBlur = () => {
    // Format the display value on blur
    if (displayValue) {
      const isoDate = parseDate(displayValue);
      if (isoDate) {
        const date = new Date(isoDate + 'T00:00:00');
        setDisplayValue(formatDate(date));
      }
    }
  };

  return (
    <div className={className}>
      {label && (
        <Label className="text-gray-400 flex items-center justify-between h-6">
          <span>{label}</span>
          <span className="text-xs text-gray-500">
            ({formatInfo.format})
          </span>
        </Label>
      )}
      <Input
        type="text"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        required={required}
        placeholder={placeholder || formatInfo.example}
        className="bg-white/5 border-white/10 text-white mt-2"
        maxLength={10}
        {...props}
      />
    </div>
  );
};

export default DateInput;
